import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { isEmpty } from 'lodash/lang';
import moment from 'moment';

import { FinBudgetExpenseFixedApi } from '../../../../../api';
import { scPurple80 } from '../../../../../constants/systemColors';
import { MANAGE_EXPENSES } from '../../../../../constants/userOperations';
import { ROLE_OPERATION_MANAGER, ROLE_STUDY_MANAGER } from '../../../../../constants/userRoles';
import { userHasAccessTo } from '../../../../../services/auth';
import { useCurrentUser } from '../../../../root/Container/CurrentUserContainer';
import { ExpenseFixedList } from '../ExpenseFixedList/ExpenseFixedList';
import { ExpenseVariableList } from '../ExpenseVariableList/ExpenseVariableList';

const StudyEventForm = ({ selectedSSU }) => {
  const [showRightPanel, setShowRightPanel] = useState(null);
  const [expensesFixed, setExpensesFixed] = useState([]);
  const [expensesAccess, setExpensesAccess] = useState(false);
  const [budgetForSSUExistForCurrentDate, setBudgetForSSUExistForCurrentDate] = useState(false);
  const [expenseVariableForSSUExistForCurrentDate, setExpenseVariableForSSUExistForCurrentDate] = useState(false);

  const [expensesVariable, setExpensesVariable] = useState([]);
  const { activeRole } = useCurrentUser();

  const updateAvailableExpenseFixed = useCallback(() => {
    FinBudgetExpenseFixedApi.getFilteredExpenseFixed(selectedSSU.ssuIdentifier).then(
      ({ data: finBudgetExpenseFixed }) => {
        setExpensesFixed(finBudgetExpenseFixed);
      }
    );
  }, [selectedSSU?.ssuIdentifier]);

  const updateAvailableExpensesVariable = useCallback(() => {
    FinBudgetExpenseFixedApi.getFilteredExpensesVariable(selectedSSU.ssuIdentifier).then(({ data }) => {
      setExpensesVariable(data);
    });
  }, [selectedSSU?.ssuIdentifier]);

  const isUserHaveAccessToManageExpenses = useMemo(() => {
    if (activeRole === ROLE_STUDY_MANAGER || activeRole === ROLE_OPERATION_MANAGER) {
      return expensesAccess;
    } else {
      return userHasAccessTo(MANAGE_EXPENSES);
    }
  }, [activeRole, expensesAccess]);

  useEffect(() => {
    if (selectedSSU?.ssuIdentifier) {
      FinBudgetExpenseFixedApi.getExpensesAccessByStudySite(selectedSSU?.ssuIdentifier).then(({ data }) => {
        setExpensesAccess(data);
      });
    }
  }, [selectedSSU?.ssuIdentifier]);

  useEffect(() => {
    if (selectedSSU?.ssuIdentifier) {
      updateAvailableExpenseFixed();
      updateAvailableExpensesVariable();
    } else {
      setExpensesFixed([]);
      setExpensesVariable([]);
    }
  }, [selectedSSU, updateAvailableExpensesVariable, updateAvailableExpenseFixed]);

  useEffect(() => {
    setShowRightPanel(null);
    if (!isEmpty(selectedSSU)) {
      FinBudgetExpenseFixedApi.findTriggersForActualBudget(
        selectedSSU.studyIdentifier,
        selectedSSU.siteIdentifier,
        selectedSSU.ssuIdentifier,
        moment()
          .utc(true)
          .format('YYYY-MM-DD')
      ).then(({ data: { finBudgetSite } }) => {
        setBudgetForSSUExistForCurrentDate(!isEmpty(finBudgetSite));
      });
      FinBudgetExpenseFixedApi.getExpenseVariableTriggers(
        selectedSSU.studyIdentifier,
        selectedSSU.siteIdentifier,
        selectedSSU.ssuIdentifier,
        moment()
          .utc(true)
          .format('YYYY-MM-DD')
      ).then(({ data: { finBudgetSite } }) => {
        setExpenseVariableForSSUExistForCurrentDate(!isEmpty(finBudgetSite));
      });
    }
  }, [selectedSSU]);

  if (
    ((!isEmpty(expensesFixed) || !isEmpty(expensesVariable)) && !isEmpty(selectedSSU)) ||
    ((budgetForSSUExistForCurrentDate || expenseVariableForSSUExistForCurrentDate) && !isEmpty(selectedSSU))
  ) {
    return (
      <>
        <Accordion disableGutters defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ color: scPurple80, fontWeight: 'bold' }}>Expenses - Fixed</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ExpenseFixedList
              expensesFixed={expensesFixed}
              updateAvailableExpenseFixed={updateAvailableExpenseFixed}
              selectedSSU={selectedSSU}
              showRightPanel={showRightPanel}
              setShowRightPanel={setShowRightPanel}
              isUserHaveAccessToManageExpenses={isUserHaveAccessToManageExpenses}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion disableGutters defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ color: scPurple80, fontWeight: 'bold' }}>Expenses - Variable</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ExpenseVariableList
              expensesVariable={expensesVariable}
              updateAvailableExpensesVariable={updateAvailableExpensesVariable}
              selectedSSU={selectedSSU}
              showRightPanel={showRightPanel}
              setShowRightPanel={setShowRightPanel}
              isUserHaveAccessToManageExpenses={isUserHaveAccessToManageExpenses}
            />
          </AccordionDetails>
        </Accordion>
      </>
    );
  }
  return (
    <div className="border px-3 pt-3 pb-1 m-0 row justify-content-between">
      <h5 className="c-p">
        {selectedSSU ? (
          <span>There is no budget for this study site</span>
        ) : (
          <span>Select site and study with existing budget</span>
        )}
      </h5>
    </div>
  );
};

export default StudyEventForm;
